/* eslint-disable @angular-eslint/prefer-inject */

import { Subscription } from 'rxjs';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  // ViewChild,
} from '@angular/core';
import { PointerEventEmitter } from '../../view-interactions/pointer-event-emitter';
import { ViewEventHandler } from '../../view-interactions/view-event-handler';
import { Visualize3dObject } from '../../models/visualize3d-object';
import { ComponentHandlerProvider } from '../../v3d-components/handlers/component-handler-provider';
import { ViewPoint } from '../../models/view-point';
import { Visualize3dViewerOptions } from '../../models/visualize3d-viewer-options';
import { globalV3dSettings } from '../../data/visualize-3d-global-settings';
import { QmodelThreejsCreatorService } from '../../providers/qmodel-threejs-creator.service';
import { Visualize3dObjectEventService } from '../../providers/visualize3d-object-event.service';
import { initVisualize3dDebug } from '../../debug/init-visualize3d-debug';
import { DebugStats } from '../../debug/create-stats';
import { Camera, Object3D } from 'three';
import { DebugRendererStats } from '../../debug/create-renderer-stats';
import { Visualize3dObjectLoadEvent, Visualize3dObjectLoadStatus } from '../../models/events/visualize3d-object-load-event';
import { V3dSceneManager } from '../../scene/v3d-scene-manager';

@Directive()
export abstract class V3dBaseViewComponent<TSceneManager extends V3dSceneManager<Camera>> implements OnChanges, AfterViewInit, OnDestroy {
  abstract container: ElementRef<HTMLElement>;

  @Input() canvasId = 'v3d-canvas';
  /**
   * Options how the user should be able to navigate the 3d scene
   */
  @Input() viewerOptions: Visualize3dViewerOptions | undefined;

  /**
   * Object to be viewed
   */
  @Input() v3dObject: Visualize3dObject | undefined;

  /**
   * Scene manager
   */
  @Input({ required: true }) sceneManager: TSceneManager;

  /**
   * Emits error messages occurred within the component
   */
  /**
   * TODO: check if Output name can be changed. Otherwise throw error
   * "Output bindings, including aliases, should not be named as standard DOM events
   * (@angular-eslint/no-output-native)"
   */
  /* eslint-disable-next-line */
  @Output() error: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Event which is emitted once after user clicks Object3D in the scene
   */
  @Output() v3dObjectClick: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event which is emitted once after user starts a drag operation
   */
  @Output() v3dObjectDragStart: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event which is emitted once after user ends a drag operation
   */
  @Output() v3dObjectDragEnd: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event which is emitted once after the pointer is released.
   */
  @Output() v3dOnUp: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event which is emitted once when the pointer is pressed.
   */
  @Output() v3dOnDown: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event which is emitted once when the pointer enters an object.
   */
  @Output() v3dOnEnter: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event which is emitted once when the pointer exits an object.
   */
  @Output() v3dOnExit: EventEmitter<Visualize3dObject> = new EventEmitter<Visualize3dObject>();

  /**
   * Event emitted for V3D object loading status
   */
  @Output() v3dObjectLoadStatus: EventEmitter<Visualize3dObjectLoadEvent> = new EventEmitter<Visualize3dObjectLoadEvent>(true);

  protected rootObject3d: Object3D | null = null;

  /**
   * Last render timestamp, used to limit FPS
   * @protected
   */
  protected lastRender = Date.now();

  // Width in pixels.
  protected canvasWidth: number;
  // Height in pixels.
  protected canvasHeight: number;

  public eventEmitter: PointerEventEmitter | null = null;
  public eventHandler: ViewEventHandler | null = null;

  private isRendering = false;

  protected debug = false;
  private debugStats: DebugStats | undefined;
  private debugRendererStats: DebugRendererStats | undefined;

  private isDisposed = false;
  private createThreeJsObjectHierarchySubscriptions: Subscription[] = [];
  private subscriptions: Subscription[] = [];

  protected constructor(
    private componentHandlerProvider: ComponentHandlerProvider,
    private qmodelThreeJsCreatorService: QmodelThreejsCreatorService,
    protected visualize3dObjectEventService: Visualize3dObjectEventService,
    protected zone: NgZone,
  ) {
    this.debug = !!globalV3dSettings.debug;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.sceneManager.scene) {
      return;
    }

    if (changes.v3dObject) {
      this.v3dObjectChanged(changes.v3dObject.previousValue, changes.v3dObject.currentValue);
    }
  }

  ngAfterViewInit(): void {
    // This would be rare if it were to happen but stranger things has happened!
    if (this.isDisposed) {
      return;
    }

    this.container.nativeElement.appendChild(this.sceneManager.renderer.domElement);

    this.subscriptions.push(this.qmodelThreeJsCreatorService.error.subscribe(error => this.error.next(error)));

    this.resetLookAt();

    this.tryInitializeDebug();
    this.initInteractionEvents();

    this.updateCanvasSize();
    this.v3dObjectChanged(undefined, this.v3dObject);
  }

  ngOnDestroy(): void {
    this.isDisposed = true;
    this.isRendering = false;

    this.rootObject3d = null;
    const subscriptions = [...this.subscriptions, ...this.createThreeJsObjectHierarchySubscriptions];
    for (const sub of subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
    this.createThreeJsObjectHierarchySubscriptions = [];

    this.eventHandler?.dispose();
    this.eventHandler = null;
    this.eventEmitter?.dispose();
    this.eventEmitter = null;
    this.debugStats = undefined;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateCanvasSize();
  }

  private tryInitializeDebug(): void {
    if (!globalV3dSettings.debug) {
      return;
    }

    const items = initVisualize3dDebug(this.sceneManager.scene, this.container.nativeElement);
    this.debugStats = items.stats;
    this.debugRendererStats = items.rendererStats;
  }

  /**
   * Initialize the pointer events
   */
  private initInteractionEvents(): void {
    if (!this.viewerOptions?.hasInteractions) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      // We create these outside angular so that the change detections aren't added to the classes.
      const eventEmitter = new globalV3dSettings.appPointerEventEmitter(
        this.sceneManager.camera,
        this.sceneManager.scene,
        this.sceneManager.renderer,
      );
      const eventHandler = new globalV3dSettings.appViewEventHandler(
        eventEmitter,
        this.componentHandlerProvider,
        this.sceneManager.scene,
        this.visualize3dObjectEventService,
      );

      this.subscriptions.push(
        eventHandler.onClick.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dObjectClick.emit(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onSelect.subscribe(v3dObject => {
          this.zone.run(() => {
            this.visualize3dObjectEventService.updateSelectedObject(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onDeselect.subscribe(() => {
          this.zone.run(() => {
            this.visualize3dObjectEventService.updateSelectedObject(undefined);
          });
          this.render(true);
        }),
        eventHandler.onDragStart.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dObjectDragStart.emit(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onDragEnd.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dObjectDragEnd.emit(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onUp.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dOnUp.emit(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onDown.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dOnDown.emit(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onEnter.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dOnEnter.emit(v3dObject);
          });
          this.render(true);
        }),
        eventHandler.onExit.subscribe(v3dObject => {
          this.zone.run(() => {
            this.v3dOnExit.emit(v3dObject);
          });
          this.render(true);
        }),
      );

      this.eventEmitter = eventEmitter;
      this.eventHandler = eventHandler;
    });

    this.sceneManager.controls.addEventListener('change', this.onControlChange);
  }

  private onControlChange = (): void => {
    this.render(true);
  };

  protected tryStartRenderLoop(): void {
    if (this.isDisposed || this.isRendering) {
      return;
    }

    this.isRendering = true;
    // If we don't run the animation loop outside angular a lot of change detection events would trigger slowing everything down.
    this.zone.runOutsideAngular(() => {
      const renderLoop = (): void => {
        if (!this.isRendering) {
          return;
        }

        this.render();

        if (this.debugStats) {
          this.debugRendererStats.update(this.sceneManager.renderer);
        }

        requestAnimationFrame(renderLoop);
      };
      renderLoop();
    });
  }

  /**
   * Internal render function. This will check for an existing FPS limit and enforce it if required. FPS limit can be bypassed
   * with se skipFrameLimit-parameter in situations like camera movement and mouse events.
   * @param skipFrameLimit
   * @protected
   */
  protected render(skipFrameLimit = false): void {
    if (this.isRendering) {
      const now = Date.now();
      const minFrameTime = 1000 / (this.viewerOptions.performance?.maxFramesPerSecond ?? globalV3dSettings.maxIdleFps);

      // If frame time is less than the minimum frame time, skip rendering
      if (!skipFrameLimit && now - this.lastRender < minFrameTime) {
        return;
      }

      this.lastRender = now;
      this.sceneManager.render();
      if (this.debugStats) {
        this.debugStats.update();
      }
    }
  }

  resetLookAt(): void {
    // The dummy bounding box will have bounds of -Infinity -> Infinity which three.js resolves at 0!
    const target = new Object3D();
    target.position.set(0, 0, 0);
    this.sceneManager.lookAt(target, ViewPoint.TopLeft, true);
  }

  updateCanvasSize(): void {
    this.canvasWidth = this.container.nativeElement.clientWidth;
    this.canvasHeight = this.container.nativeElement.clientHeight;
    this.sceneManager.setCanvasSize(this.canvasWidth, this.canvasHeight);
    this.sceneManager.updateCameraAspectRatio();
  }

  v3dObjectChanged(previousV3dObject: Visualize3dObject | undefined, currentV3dObject: Visualize3dObject | undefined): void {
    // Due to async nature of the object creation, unsubscribe any possible other sync operations
    for (const sub of this.createThreeJsObjectHierarchySubscriptions) {
      sub.unsubscribe();
    }
    this.createThreeJsObjectHierarchySubscriptions = [];

    if (currentV3dObject) {
      this.v3dObjectLoadStatus.emit({ status: Visualize3dObjectLoadStatus.LoadStarted });

      this.createThreeJsObjectHierarchySubscriptions.push(
        this.qmodelThreeJsCreatorService.createThreeJsObjectHierarchy(currentV3dObject, null).subscribe(object3d => {
          // In case the scene is disposed.
          if (this.isDisposed) {
            return;
          }

          // Do not render the scene until the update is complete to avoid flickering
          this.isRendering = false;

          // Remove the old object from the scene before we load a new one.
          this.sceneManager.setRootObject(object3d);

          // Finally try and set up the render loop!
          if (!this.isRendering) {
            this.tryStartRenderLoop();
          }

          this.visualize3dObjectEventService.v3dObjectChanged.next({
            previous: previousV3dObject,
            current: currentV3dObject,
          });

          this.v3dObjectLoadStatus.emit({ status: Visualize3dObjectLoadStatus.Loaded });
        }),
      );
    }
  }
}
